<template>
  <page-stepped
    ref="stepper"
    :breadcrumbs="breadcrumbs"
    :title="title"
    :steps="steps"
    :model="model"
    :fetch-endpoint="fetchEndpoint"
    :save-endpoint="saveEndpoint"
    :after-save="afterSave"
    :rules="rules"
    :readonly="readonly"
  >
    <template #pre-actions>
      <el-button
        v-if="isEditPage"
        type="primary"
        plain
        @click="goToRunHistory"
        data-qa="run-history-button"
      >
        <i class="el-icon-time"></i>
        {{ $t('Automations_RunHistory') }}
      </el-button>
    </template>
  </page-stepped>
</template>
<script>
import PageStepped from '@/common/components/page/PageStepped'
import StepImportDataSource from '@/modules/imports/components/StepImportDataSource'
import StepImportDataMapping from '@/modules/imports/components/StepImportDataMapping'
import StepImportSchedule from '@/modules/automations/components/StepImportSchedule'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { sourceTypesLabels } from '@/modules/imports/enum/ImportsEnum'
import { validationMixin } from '@/modules/imports/mixins/ValidationMixin'
import { attributesMixin } from '@/modules/imports/mixins/AttributesMixin'
import iframeTitleMixin from '@/common/mixins/iframeTitleMixin'

export default {
  name: 'ImportFormPage',

  mixins: [validationMixin, attributesMixin, iframeTitleMixin],

  components: {
    PageStepped
  },

  data() {
    return {
      sourceTypesLabels,
      model: this.defaultModel(),
      steps: [
        {
          name: 'data_source',
          title: this.$t('Automations_Tab_DataSource'),
          component: StepImportDataSource,
          completed: false,
          rules: {
            name: [
              {
                required: true,
                message: this.$t('RestForm_FieldIsRequired')
              },
              {
                validator: this.validateAutomationName
              }
            ],
            config: {
              event: [
                {
                  validator: this.validateEventField
                }
              ],
              columns: [
                {
                  type: 'array',
                  required: true,
                  message: this.$t('RestForm_SchemaIsRequired'),
                  min: 1
                },
                {
                  validator: this.validateUserIdCol
                },
                {
                  validator: this.validateEventCol
                },
                {
                  validator: this.validateColumns
                }
              ]
            },
            source: {
              type: {
                required: true,
                message: this.$t('RestForm_FieldIsRequired')
              },
              integration_id: {
                required: true,
                message: this.$t('RestForm_FieldIsRequired')
              },
              url: [
                {
                  required: true,
                  message: this.$t('RestForm_FieldIsRequired')
                },
                {
                  validator: this.validateUrl
                }
              ],
              file_name: {
                required: true,
                message: this.$t('RestForm_FieldIsRequired')
              },
              delta_column: {
                validator: this.validateDeltaColumn
              }
            }
          },
          props: {}
        },
        {
          name: 'data_mapping',
          title: this.$t('Automations_Tab_DataMapping'),
          component: StepImportDataMapping,
          completed: false,
          rules: {},
          props: {
            attributes: [],
            filterableAttributes: [],
            integrations: {}
          }
        },
        {
          name: 'schedule',
          title: this.$t('Automations_Tab_Schedule'),
          component: StepImportSchedule,
          completed: false,
          rules: {},
          props: {}
        }
      ],
      isModelLoaded: false
    }
  },
  mounted() {
    this.loadSourceEnum()
    if (!this.isEditPage) {
      this.setFilterableAttributes()
    }
  },
  computed: {
    embeddedPageTitle() {
      return this.isEditPage && this.model ? this.model.name : null
    },

    title() {
      if (this.$store.getters.embeddedTitle) {
        return this.$store.getters.embeddedTitle
      }
      return this.isEditPage
        ? this.model.name
        : this.$t('Automations_Title_CreateAutomation')
    },

    isEditPage() {
      return !!this.$route.query.id
    },

    fetchEndpoint() {
      return [
        this.fetchAutomationsEndpoint,
        this.fsIntegrationsEndpoint,
        this.dbIntegrationsEndpoint,
        this.attributesEndpoint
      ]
    },

    fetchAutomationsEndpoint() {
      if (!this.isEditPage) return null

      return projectEndpoint({
        method: 'get',
        url: `import-automations/${this.$route.query.id}`,
        handler: data => {
          this.model = data
          this.$nextTick(() => {
            this.setFilterableAttributes()
            this.isModelLoaded = true
          })
        }
      })
    },

    fsIntegrationsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `integrations`,
        params: {
          type: 'sftp,gcs,s3',
          active: 1
        },
        handler: data => {
          this.$set(this.steps[0].props, 'integrations', data)
        }
      })
    },

    dbIntegrationsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `integrations`,
        params: {
          type: 'snowflake',
          active: 1
        },
        handler: data => {
          this.$set(this.steps[0].props, 'dbIntegrations', data)
        }
      })
    },

    attributesEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: {
          sort: 'title',
          attribute_profile: 1
        },
        handler: data => {
          this.steps[1].props.attributes = data
        }
      })
    },

    saveEndpoint() {
      let method = this.isEditPage ? 'patch' : 'post'
      return projectEndpoint({
        method: method,
        url: this.isEditPage
          ? `import-automations/${this.model.id}`
          : `import-automations`
      })
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('Automations_Breadcrumb_Data')
        },
        {
          label: this.$t('Automations_Breadcrumb_Automations')
        },
        {
          label: this.$t('Automations_Breadcrumb_Imports'),
          route: projectRoute({
            name: 'AutomationImports'
          })
        },
        { label: this.title }
      ]
    },

    rules() {
      return this.steps.reduce(
        (obj, item) => Object.assign(obj, item.rules),
        {}
      )
    },

    readonly() {
      return (
        !this.$can('manageAutomation') ||
        this.$route.name === 'AutomationImportView'
      )
    },

    currentStepName() {
      return this.$refs.stepper.currentStep.name
    }
  },

  methods: {
    afterSave(data) {
      const id = data.id || this.$route.query.id
      this.$router.push(
        projectRoute({
          name: 'AutomationImportRunHistorySyncs',
          query: { id }
        })
      )
    },

    goToRunHistory() {
      this.$router.push(
        projectRoute({
          name: 'AutomationImportRunHistorySyncs',
          query: { id: this.$route.query.id }
        })
      )
    },

    defaultModel() {
      if (this.isEditPage) {
        return null
      } else {
        return {
          name: '',
          type: 'profiles',
          source: {
            type: '',
            sync_mode: 'full',
            delta_column: '',
            row_limit: 1000000
          },
          config: {
            columns: [],
            triggering: { campaign_id: null },
            event: ''
          },
          schedule: {
            frequency: 'daily',
            time: '00:00'
          },
          active: 1
        }
      }
    },

    loadSourceEnum() {
      let sources = Object.fromEntries(
        Object.entries(this.sourceTypesLabels).filter(
          ([key, value]) => key === 'fs' || key === 'db'
        )
      )

      this.$set(this.steps[0].props, 'sources', sources)
    }
  },

  watch: {
    'model.type': {
      handler() {
        if ((this.isEditPage && this.isModelLoaded) || !this.isEditPage) {
          this.setFilterableAttributes()
          this.model.config.columns = []
        }
      }
    },

    'model.config.event': {
      handler() {
        if ((this.isEditPage && this.isModelLoaded) || !this.isEditPage) {
          this.setFilterableAttributes()
          this.model.config.columns = []
        }
      }
    }
  }
}
</script>
<style scoped>
>>> .Profile_Attribute_Input_Error {
  border-color: #f56c6c;
}
</style>



// WEBPACK FOOTER //
// src/modules/automations/pages/ImportFormPage.vue